import formatISO from "date-fns/formatISO";
import isDate from "lodash/isDate";

import type { PatchTaskRequestV3 } from "@fartherfinance/frontend/api/Tasks/requests/patchTask.v3";
import { TaskV3 } from "@fartherfinance/frontend/api/Tasks/Types";
import {
  mapTaskPriorityToLabel,
  mapTaskStatusToLabel,
  mapTaskTypeToLabelV2,
} from "@fartherfinance/frontend/api/Tasks/utils/mappings";
import { ServiceTeamId } from "@fartherfinance/frontend/api/Types";

import { assigneeDefaultOption } from "../../../common";
import { mapAssigneeToOption } from "../../../utils";
import { EnhancedAutocompleteOption } from "@src/multiCustodian/pages/Advisor/common";
import { mapPersonToOption } from "@src/multiCustodian/pages/Advisor/utils";

import { type TaskDetailsFormDataV3 } from "./TaskDetailsFormProvider.types";

export const mapTaskDetailsToFormDataV3 = (
  {
    taskStatus,
    taskPriority,
    taskType,
    serviceTeamId,
    assignee,
    client,
    advisor,
    dueDate,
    custodian,
  }: TaskV3,
  serviceTeamOptions: EnhancedAutocompleteOption<ServiceTeamId>[]
): TaskDetailsFormDataV3 => {
  return {
    taskStatus: { label: mapTaskStatusToLabel(taskStatus), value: taskStatus },
    taskPriority: {
      label: mapTaskPriorityToLabel(taskPriority),
      value: taskPriority,
    },
    taskType: { label: mapTaskTypeToLabelV2(taskType), value: taskType },
    serviceTeam:
      serviceTeamOptions.find(
        (serviceTeamOption) => serviceTeamOption.value === serviceTeamId
      ) ?? null,
    assignee: assignee ? mapAssigneeToOption(assignee) : assigneeDefaultOption,
    client: client && mapPersonToOption(client),
    advisor: advisor && mapPersonToOption(advisor),
    dueDate,
    custodian: custodian && { label: custodian, value: custodian },
  };
};

export const mapFormDataToPayloadV3 = (
  formData: TaskDetailsFormDataV3,
  fieldName: keyof TaskDetailsFormDataV3
): PatchTaskRequestV3 => {
  const mappedKey = {
    serviceTeam: "serviceTeamId",
    assignee: "assigneeId",
    client: "clientId",
    advisor: "advisorId",
    taskType: "taskType",
    taskStatus: "taskStatus",
    taskPriority: "taskPriority",
    dueDate: "dueDate",
    custodian: "custodian",
  }[fieldName];

  const fieldValue = formData[fieldName];
  const isFieldNullable =
    mappedKey === "clientId" ||
    mappedKey === "custodian" ||
    mappedKey === "assigneeId" ||
    mappedKey === "serviceTeamId";
  const isValueObject =
    fieldValue !== null &&
    typeof fieldValue === "object" &&
    "value" in fieldValue;

  // NOTE: assigneeId needs special handling as it's structure is different than other options
  if (mappedKey === "assigneeId" && isValueObject && "meta" in fieldValue) {
    const value =
      fieldValue.value === undefined
        ? null
        : {
            type: fieldValue.meta.type,
            id: fieldValue.value,
          };

    return {
      [mappedKey]: { value },
    };
  }

  // NOTE: dueDate needs special handling as we need to format it to ISO date
  if (mappedKey === "dueDate" && isDate(fieldValue)) {
    return {
      [mappedKey]: formatISO(fieldValue, { representation: "date" }),
    };
  }

  // prettier-ignore
  const value = fieldValue && isValueObject
    ? isFieldNullable ? { value: fieldValue.value } : fieldValue.value
    : isFieldNullable ? { value: fieldValue } : fieldValue

  return { [mappedKey]: value };
};
